import { useEffect, useState } from "react";
import { api, extractError } from "../lib/api";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "./ui/dialog";
import { Loader2, AlertTriangle, ExternalLink } from "lucide-react";

/** Pretty-print Alvys's nested `{ Amount, Currency }` money object. */
function fmtAlvysAmount(m) {
  if (m == null) return "—";
  if (typeof m === "number") return `$${m.toFixed(2)}`;
  if (typeof m === "object" && "Amount" in m) {
    const n = Number(m.Amount || 0);
    const sign = n < 0 ? "-" : "";
    return `${sign}$${Math.abs(n).toFixed(2)}`;
  }
  return String(m);
}

function fmtDt(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

function DeductionCard({ ref_id, state }) {
  if (state.loading) {
    return (
      <div data-testid={`ded-loading-${ref_id}`} className="flex items-center gap-2 rounded-md border border-slate-200 bg-slate-50 p-3 text-sm text-slate-600">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading {ref_id.slice(0, 12)}…
      </div>
    );
  }
  if (state.error) {
    const is404 = (state.status === 404) || /not found/i.test(state.error || "");
    return (
      <div data-testid={`ded-error-${ref_id}`}
        className={`rounded-md border p-3 text-sm ${
          is404 ? "border-rose-300 bg-rose-50 text-rose-800" : "border-amber-300 bg-amber-50 text-amber-900"
        }`}>
        <div className="flex items-center gap-2 font-medium">
          <AlertTriangle className="h-4 w-4" />
          {is404 ? "Deduction not found in Alvys — may have been deleted" : "Could not fetch from Alvys"}
        </div>
        <div className="mt-1 break-all font-mono text-xs opacity-80">ref: {ref_id}</div>
        {!is404 && <div className="mt-1 text-xs">{state.error}</div>}
      </div>
    );
  }
  const d = state.data || {};
  const rows = [
    ["Id", d.Id || ref_id],
    ["Driver Id", d.DriverId],
    ["Amount", fmtAlvysAmount(d.Amount)],
    ["Category", d.Category],
    ["Description", d.Description],
    ["Date", fmtDt(d.Date)],
    ["Created At", fmtDt(d.CreatedAt)],
    ["Is Paid", d.IsPaid === true ? "Yes" : d.IsPaid === false ? "No" : "—"],
  ];
  return (
    <div data-testid={`ded-card-${ref_id}`} className="rounded-md border border-slate-200 bg-white p-3">
      <div className="mb-2 flex items-center gap-2">
        <ExternalLink className="h-4 w-4 text-slate-500" />
        <span className="font-mono text-xs text-slate-500">{ref_id}</span>
      </div>
      <dl className="grid grid-cols-[8rem_1fr] gap-x-3 gap-y-1 text-sm">
        {rows.map(([k, v]) => (
          <div key={k} className="contents">
            <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">{k}</dt>
            <dd className="break-words text-slate-800">{v ?? "—"}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

/**
 * Renders a modal showing Alvys deduction data for one or more external_refs.
 * Pass `refs` as an array of strings (or comma-joined string) and `open`.
 */
export default function DeductionDialog({ open, onOpenChange, refs, title = "Alvys Deduction" }) {
  const refList = Array.isArray(refs)
    ? refs.filter(Boolean)
    : (refs || "").split(",").map((s) => s.trim()).filter(Boolean);
  // Map<ref, { loading, data, error, status }>
  const [state, setState] = useState({});

  useEffect(() => {
    if (!open || refList.length === 0) return;
    let cancelled = false;
    setState(Object.fromEntries(refList.map((r) => [r, { loading: true }])));
    (async () => {
      for (const ref of refList) {
        try {
          const r = await api.get(`/deductions/${encodeURIComponent(ref)}`);
          if (!cancelled) setState((s) => ({ ...s, [ref]: { loading: false, data: r.data } }));
        } catch (e) {
          if (!cancelled) {
            setState((s) => ({
              ...s,
              [ref]: { loading: false, error: extractError(e), status: e?.response?.status },
            }));
          }
        }
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, refs]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="deduction-dialog" className="max-w-xl">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            Live data from Alvys via <code className="rounded bg-slate-100 px-1 py-0.5 text-xs">GET /api/deductions/&#123;ref&#125;</code>.
            {refList.length > 1 && ` Showing ${refList.length} deductions.`}
          </DialogDescription>
        </DialogHeader>
        <div className="max-h-[60vh] space-y-2 overflow-y-auto">
          {refList.length === 0 ? (
            <p className="text-sm text-slate-500">No external references on this expense.</p>
          ) : refList.map((r) => (
            <DeductionCard key={r} ref_id={r} state={state[r] || { loading: true }} />
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
